/**
 * VIP AI Chatbot Component
 * 
 * Elite-only feature providing 24/7 AI-powered skin care support
 */

import React, { useState, useRef, useEffect } from 'react';
import styled from 'styled-components';
import { theme } from '../styles/theme';
import { Card } from './ui/Card';
import { Button } from './ui/Button';
import { MessageCircle, X, Send, Bot, User, Sparkles } from 'lucide-react';
import { useSubscription } from '../hooks/useSubscription';
import { useAWSAuth } from '../contexts/AWSAuthContext';
import { useLatestScan } from '../hooks/useLatestScan';
import { getRecommendedProductsFromScan } from '../utils/productRecommendations';
import { callOpenAI } from '../services/openaiService';

const ChatButton = styled.button<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 20px;
  right: 20px;
  width: 60px;
  height: 60px;
  border-radius: 50%;
  background: linear-gradient(135deg, ${theme.colors.status.warning} 0%, ${theme.colors.primary[600]} 100%);
  border: none;
  color: white;
  cursor: pointer;
  display: flex;
  align-items: center;
  justify-content: center;
  box-shadow: 0 4px 20px rgba(0, 0, 0, 0.3);
  z-index: 1000;
  transition: all 0.3s ease;
  
  &:hover {
    transform: scale(1.1);
    box-shadow: 0 6px 25px rgba(0, 0, 0, 0.4);
  }

  svg {
    width: 24px;
    height: 24px;
  }
`;

const ChatContainer = styled(Card)<{ $isOpen: boolean }>`
  position: fixed;
  bottom: 90px;
  right: 20px;
  width: 380px;
  height: 600px;
  max-height: calc(100vh - 120px);
  display: ${props => props.$isOpen ? 'flex' : 'none'};
  flex-direction: column;
  z-index: 1001;
  box-shadow: 0 10px 40px rgba(0, 0, 0, 0.3);
  padding: 0;
  overflow: hidden;
  min-height: 0;

  @media (max-width: 1023px) {
    top: 0;
    left: 0;
    right: 0;
    bottom: 0;
    width: 100vw;
    height: 100vh;
    height: 100dvh;
    max-height: 100dvh;
    border-radius: 0;
    box-shadow: none;
    z-index: 9999;
  }
`;

const ChatHeader = styled.div`
  flex-shrink: 0;
  background: linear-gradient(135deg, ${theme.colors.status.warning} 0%, ${theme.colors.primary[600]} 100%);
  color: white;
  padding: 1rem 1.5rem;
  display: flex;
  align-items: center;
  justify-content: space-between;
  border-bottom: 1px solid rgba(255, 255, 255, 0.2);
`;

const ChatHeaderContent = styled.div`
  display: flex;
  align-items: center;
  gap: 0.75rem;
`;

const ChatTitle = styled.h3`
  font-family: ${theme.typography.fontFamily.secondary};
  font-size: ${theme.typography.fontSize.lg};
  font-weight: ${theme.typography.fontWeight.bold};
  margin: 0;
  color: white;
`;

const VIPBadge = styled.span`
  background: rgba(255, 255, 255, 0.3);
  padding: 0.25rem 0.5rem;
  border-radius: ${theme.borderRadius.md};
  font-size: ${theme.typography.fontSize.xs};
  font-weight: ${theme.typography.fontWeight.semibold};
`;

const CloseButton = styled.button`
  background: rgba(255, 255, 255, 0.2);
  border: none;
  border-radius: ${theme.borderRadius.md};
  color: white;
  cursor: pointer;
  padding: 0.5rem;
  display: flex;
  align-items: center;
  justify-content: center;
  transition: background 0.2s;

  &:hover {
    background: rgba(255, 255, 255, 0.3);
  }

  svg {
    width: 18px;
    height: 18px;
  }
`;

const MessagesContainer = styled.div`
  flex: 1;
  min-height: 0;
  overflow-y: auto;
  padding: 1.5rem;
  display: flex;
  flex-direction: column;
  gap: 1rem;
  background: ${theme.colors.background.primary};
  -webkit-overflow-scrolling: touch;
`;

const Message = styled.div<{ $isUser: boolean }>`
  display: flex;
  gap: 0.75rem;
  align-items: flex-start;
  flex-direction: ${props => props.$isUser ? 'row-reverse' : 'row'};
`;

const MessageAvatar = styled.div<{ $isUser: boolean }>`
  width: 32px;
  height: 32px;
  border-radius: 50%;
  display: flex;
  align-items: center;
  justify-content: center;
  flex-shrink: 0;
  background: ${props => props.$isUser 
    ? theme.colors.primary[500] 
    : `linear-gradient(135deg, ${theme.colors.status.warning} 0%, ${theme.colors.primary[600]} 100%)`};
  color: white;

  svg {
    width: 18px;
    height: 18px;
  }
`;

const MessageBubble = styled.div<{ $isUser: boolean }>`
  max-width: 92%;
  padding: 1.25rem 1.5rem;
  border-radius: ${theme.borderRadius.lg};
  background: ${props => props.$isUser 
    ? theme.colors.primary[500] 
    : theme.colors.background.secondary};
  color: ${props => props.$isUser ? 'white' : theme.colors.text.primary};
  font-size: ${theme.typography.fontSize.base};
  line-height: 1.7;
  word-wrap: break-word;
  word-break: break-word;
  overflow-wrap: break-word;
  white-space: pre-wrap;

  @media (max-width: 1023px) {
    max-width: 94%;
    padding: 1.25rem 1.5rem;
    font-size: ${theme.typography.fontSize.base};
    line-height: 1.75;
  }
`;

const InputContainer = styled.div`
  flex-shrink: 0;
  padding: 1rem 1.5rem;
  padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
  border-top: 1px solid ${theme.colors.border.light};
  background: ${theme.colors.background.primary};
  display: flex;
  gap: 0.75rem;
  align-items: flex-end;
  min-height: 0;

  @media (max-width: 1023px) {
    padding: 1rem;
    padding-bottom: calc(1rem + env(safe-area-inset-bottom, 0px));
    background: #ffffff;
  }
`;

const Input = styled.textarea`
  flex: 1;
  min-width: 0;
  padding: 0.75rem 1rem;
  border: 1px solid ${theme.colors.border.light};
  border-radius: ${theme.borderRadius.lg};
  font-size: ${theme.typography.fontSize.sm};
  font-family: ${theme.typography.fontFamily.primary};
  background: ${theme.colors.background.primary};
  color: ${theme.colors.text.primary};
  resize: none;
  min-height: 44px;
  max-height: 120px;
  line-height: 1.5;

  &:focus {
    outline: none;
    border-color: ${theme.colors.primary[500]};
  }

  &::placeholder {
    color: ${theme.colors.text.tertiary};
  }
`;

const SendButton = styled(Button)`
  flex-shrink: 0;
  padding: 0.75rem;
  min-width: 44px;
  width: 44px;
  height: 44px;
  display: flex;
  align-items: center;
  justify-content: center;
`;

const TypingIndicator = styled.div`
  display: flex;
  gap: 0.5rem;
  align-items: center;
  padding: 1rem 1.25rem;
  background: ${theme.colors.background.secondary};
  border-radius: ${theme.borderRadius.lg};
  max-width: 90px;

  span {
    width: 8px;
    height: 8px;
    border-radius: 50%;
    background: ${theme.colors.text.tertiary};
    animation: typing 1.4s infinite;

    &:nth-child(2) {
      animation-delay: 0.2s;
    }

    &:nth-child(3) {
      animation-delay: 0.4s;
    }
  }

  @keyframes typing {
    0%, 60%, 100% {
      transform: translateY(0);
      opacity: 0.7;
    }
    30% {
      transform: translateY(-10px);
      opacity: 1;
    }
  }
`;

interface Message {
  id: string;
  text: string;
  isUser: boolean;
  timestamp: Date;
}

/**
 * AI Chatbot responses - Simulated AI responses for skin care questions
 * Provides direct, specific answers to user questions
 */
// Helper function to extract skin type from scan data
const getSkinTypeFromScan = (scan: any): 'oily' | 'dry' | 'combination' | 'sensitive' | 'normal' | null => {
  if (!scan?.analysis) return null;
  
  const estimatedPH = scan.analysis.acneAnalysis?.estimatedPH ?? scan.analysis.estimatedPH;
  const textureNotes = (scan.analysis.acneAnalysis?.textureSummary || scan.analysis.textureNotes || '').toLowerCase();
  
  if (typeof estimatedPH === 'number') {
    if (estimatedPH < 4.7 || textureNotes.includes('sensitive')) {
      return 'sensitive';
    } else if (estimatedPH > 5.9 || textureNotes.includes('dry')) {
      return 'dry';
    } else if (estimatedPH < 5.2 || textureNotes.includes('oily')) {
      return 'oily';
    } else if (textureNotes.includes('combination') || textureNotes.includes('t-zone')) {
      return 'combination';
      } else {
      return 'normal';
    }
  }
  
  // Fallback to texture notes
  if (textureNotes.includes('sensitive') || textureNotes.includes('irritated')) return 'sensitive';
  if (textureNotes.includes('dry') || textureNotes.includes('flaky')) return 'dry';
  if (textureNotes.includes('oily') || textureNotes.includes('shiny')) return 'oily';
  if (textureNotes.includes('combination')) return 'combination';
  
  return null;
};

// Helper function to get acne severity from scan
const getAcneSeverityFromScan = (scan: any): string | null => {
  if (!scan?.analysis) return null;
  const severity = scan.analysis.acneAnalysis?.overallSeverity || scan.analysis.acneSeverity;
  return severity || null;
};

// Helper function to get glow score from scan
const getGlowScoreFromScan = (scan: any): number | null => {
  if (!scan) return null;
  return scan.analysis?.skinHealthIndex ?? scan.glowScore ?? null;
};

// Helper function to get usage instructions for a product based on concern
// Includes ingredient safety + pairing rules
const getUsageInstructions = (product: any, concern?: string): string => {
  const productType = product.productCategory?.toLowerCase() || '';
  const productName = (product.brand + ' ' + product.productCategory).toLowerCase();
  const isAcne = concern?.includes('acne') || concern?.includes('blackhead');
  const isDarkSpots = concern?.includes('dark spot') || concern?.includes('hyperpigmentation');
  const isDry = concern?.includes('dry');
  const isSensitive = concern?.includes('sensitive');
  
  // Check for specific ingredients for safety rules
  const hasRetinol = product.keyIngredients?.some((ing: string) => 
    ing.toLowerCase().includes('retinol') || ing.toLowerCase().includes('retinal')
  );
  const hasAHA = product.keyIngredients?.some((ing: string) => 
    ing.toLowerCase().includes('glycolic') || ing.toLowerCase().includes('lactic') || ing.toLowerCase().includes('aha')
  );
  const hasBHA = product.keyIngredients?.some((ing: string) => 
    ing.toLowerCase().includes('salicylic') || ing.toLowerCase().includes('bha')
  );
  const hasVitaminC = product.keyIngredients?.some((ing: string) => 
    ing.toLowerCase().includes('vitamin c') || ing.toLowerCase().includes('ascorbic')
  );
  const hasCentella = product.keyIngredients?.some((ing: string) => 
    ing.toLowerCase().includes('centella') || ing.toLowerCase().includes('cica')
  );
  const hasNiacinamide = product.keyIngredients?.some((ing: string) => 
    ing.toLowerCase().includes('niacinamide')
  );
  
  if (productType.includes('cleanser')) {
    return 'Use twice daily (morning and evening). Apply to damp skin, massage gently for 30 seconds, then rinse thoroughly with lukewarm water.';
  } else if (productType.includes('toner')) {
    if (hasBHA || hasAHA) {
      return 'Use after cleansing, 2-3 times per week initially (evening recommended). Apply with a cotton pad or pat in with hands. MUST pair with sunscreen during the day. Avoid if skin feels irritated.';
    }
    if (isAcne) {
      return 'Use after cleansing, 2-3 times per week initially. Apply with a cotton pad or pat in with hands. Avoid if skin feels irritated.';
    }
    return 'Use after cleansing, morning and/or evening. Apply with a cotton pad or pat in with hands.';
  } else if (productType.includes('serum')) {
    if (hasRetinol) {
      return 'Apply in the evening only (PM), 2-3 times per week initially. Wait 20-30 minutes after cleansing. Increase frequency gradually. Avoid with AHA/BHA on the same night. Always use sunscreen during the day.';
    } else if (hasVitaminC) {
      return 'Apply in the morning (AM recommended) after cleansing, before moisturizer. Start with 2-3 times per week, increase to daily if tolerated. Always follow with sunscreen.';
    } else if (hasAHA || hasBHA) {
      return 'Apply in the evening, 1-2 times per week initially. Start with once per week. Apply after cleansing, wait 20 minutes, then apply moisturizer. MUST pair with sunscreen during the day. Avoid on sensitive or irritated skin.';
    } else if (hasCentella || hasNiacinamide) {
      return 'Apply after cleansing, before moisturizer. Can be used morning and/or evening. Safe for most skin types, including sensitive. Start with 2-3 times per week if new to actives.';
    } else if (isDarkSpots) {
      return 'Apply in the morning after cleansing, before moisturizer. Start with 2-3 times per week, increase to daily if tolerated. Always follow with sunscreen.';
    } else if (isAcne) {
      return 'Apply after cleansing, 2-3 times per week initially. Increase to daily if tolerated. Avoid if skin feels irritated.';
    }
    return 'Apply after cleansing, before moisturizer. Can be used morning and/or evening. Start with 2-3 times per week if new to actives.';
  } else if (productType.includes('moisturizer')) {
    if (isDry) {
      return 'Apply morning and evening after serums. Use on damp skin to lock in moisture. Can layer with face oil if needed.';
    }
    return 'Apply morning and evening as the final step (before sunscreen in AM). Use a pea-sized amount for face and neck.';
  } else if (productType.includes('sunscreen') || productType.includes('spf')) {
    return 'Apply every morning as the final step, 15-30 minutes before sun exposure. Reapply every 2 hours if outdoors. Use 1/4 teaspoon for face and neck. REQUIRED when using AHA/BHA/Vitamin C/Retinol.';
  } else if (productType.includes('exfoliant') || productType.includes('acid')) {
    return 'Use 1-2 times per week in the evening. Start with once per week. Apply after cleansing, wait 20 minutes, then apply moisturizer. MUST pair with sunscreen during the day. Avoid on sensitive or irritated skin. Do not use with retinol on the same night.';
  }
  
  return 'Follow the product instructions. Start with 2-3 times per week if new to actives. Always patch test first.';
};

export const generateAIResponse = async (
  userMessage: string, 
  userProfile: any, 
  latestScan?: any,
  conversationHistory?: Array<{ text: string; isUser: boolean }>,
  visibleProducts?: Array<any>, // Products currently visible on screen (from Results/Curated/View All)
  imageDataUrl?: string | null   // Optional: base64 image for product/ingredient questions
): Promise<string> => {
  const lowerMessage = userMessage.toLowerCase();
  
  // SAFETY CHECK: Detect severe symptoms and recommend professional care
  const hasSevereSymptoms = lowerMessage.includes('severe pain') ||
                           lowerMessage.includes('rapidly worsening') ||
                           lowerMessage.includes('infection') ||
                           lowerMessage.includes('swelling') ||
                           lowerMessage.includes('severe rash') ||
                           lowerMessage.includes('burning sensation') ||
                           lowerMessage.includes('open wound') ||
                           lowerMessage.includes('pus') ||
                           lowerMessage.includes('fever') ||
                           lowerMessage.includes('allergic reaction');
  
  if (hasSevereSymptoms) {
    return `I'm not a doctor, and this sounds like it needs immediate professional attention. Please stop using any products that may be causing irritation and see a dermatologist or doctor in person right away, especially if you're experiencing severe pain, rapidly worsening symptoms, signs of infection, or an allergic reaction.\n\n**For urgent skin concerns:**\n• Stop using any new products immediately\n• See a dermatologist or visit urgent care\n• If it's an allergic reaction (swelling, difficulty breathing), seek emergency care\n\nI can help with general skincare guidance, but serious symptoms require professional medical evaluation.`;
  }
  
  // Extract user's skin data from latest scan
  const userSkinType = latestScan ? getSkinTypeFromScan(latestScan) : null;
  const userAcneSeverity = latestScan ? getAcneSeverityFromScan(latestScan) : null;
  const userGlowScore = latestScan ? getGlowScoreFromScan(latestScan) : null;
  
  // Get recommended products for context - filter by gender
  const userGender = userProfile?.gender;
  const recommendedProducts = latestScan ? getRecommendedProductsFromScan(latestScan, 20, userGender) : [];
  
  // Build system prompt with all rules and context
  let systemPrompt = `You are Glow Bot, an expert skincare and nutrition assistant. You have comprehensive knowledge about skincare products, ingredients, routines, diet, nutrition, and all related topics.

SCOPE - ANSWER ANY OF THESE:
• Skincare: products, brands, ingredients, routines, AM/PM regimens, cleansers, serums, moisturizers, sunscreen, retinol, acids, oils, masks, treatments
• Diet & nutrition: foods for skin health, acne-friendly diets, anti-inflammatory foods, hydration, vitamins (A/C/E/Zn), omega-3, glycemic index, dairy and skin, sugar and skin
• Skin conditions: acne, hyperpigmentation, dryness, oiliness, sensitivity, aging, dark circles, texture
• Product comparisons, recommendations, how to use, layering order, patch testing
• General wellness: sleep and skin, stress and skin, exercise, supplements

CRITICAL: You MUST answer ANY question that touches skincare, diet, nutrition, or skin health. NEVER say "I don't have that in my database", "I can't answer that", or "I don't have access to that information". You have extensive knowledge - USE IT. If a question is even loosely related to skin or diet, answer it fully.

CRITICAL FORMATTING: Format responses EXACTLY like ChatGPT does
• Use clean, well-spaced bullet points with proper indentation
• Use markdown formatting: **bold** for emphasis, *italic* for subtle emphasis
• Use proper line breaks between sections (blank lines)
• Use numbered lists (1., 2., 3.) for sequential steps
• Use bullet points (- or •) for lists of items
• Use sub-bullets with proper indentation (2 spaces or tab)
• Keep paragraphs short (2-3 sentences max)
• Use clear section headers with **bold** or ## formatting
• Make it scannable and easy to read

RULES:
1. Answer EVERY question using your comprehensive knowledge of skincare and nutrition - you know about ALL products, brands, ingredients, and topics
2. NEVER say "I don't have information in my database" or "I can't find information" - you have extensive knowledge, USE IT
3. Use the user's EXACT spelling for product and brand names. Do NOT auto-correct or change their spelling. If they write "Based bodyworks", use "Based bodyworks" in your response—not "Basis". If they write "CeraVe" or "cerave", use their spelling. This applies to ALL products.
4. The recommended products list below is ONLY for reference when users ask about "my recommendations" - it does NOT limit what you can discuss
5. Use your knowledge to answer questions about:
   • ANY skincare products or brands
   • ANY ingredients and their benefits
   • Skincare routines and best practices
   • Dietary questions and nutrition
   • Skin conditions and concerns
   • Product comparisons and recommendations
6. Identify the output type the user wants:
   • Diet plan → give a 7-day structured menu (breakfast/lunch/dinner)
   • Routine → give AM/PM steps in correct order
   • Product questions → answer using your knowledge of that product/brand
   • DIY mask → give ingredients + steps + how often
   • Explanation/"why" → give ingredient/science reasoning
7. Do NOT replace the requested output with generic tips
8. No repeating product lists unless asked
9. Never restart with a greeting after first message
10. If any detail is missing → ask a quick clarifying question instead of guessing

FORMAT EXAMPLES (ChatGPT-style):

Diet Plan Example:
**Day 1:**
- **Breakfast:** [food] - [skin benefit]
- **Lunch:** [food] - [skin benefit]
- **Dinner:** [food] - [skin benefit]

*Hydration: 1 gallon of water daily (for acne-prone skin: helps flush toxins, reduce inflammation, plump skin cells, and support healing). Otherwise: 8-10 glasses.*

Routine Example:
**Morning Routine (AM):**
1. Cleanser - [product/ingredient]
2. Serum - [product/ingredient]
3. Moisturizer - [product/ingredient]
4. Sunscreen - [product/ingredient]

**Evening Routine (PM):**
1. Cleanser - [product/ingredient]
2. Treatment - [product/ingredient]
3. Moisturizer - [product/ingredient]

Product Example:
**1. Product Name — $Price**
- **Why it helps:** [explanation]
- **Key ingredients:** [list]
- **How to use:** [instructions]

DIY Mask Example:
**Ingredients:**
- [ingredient 1]
- [ingredient 2]
- [ingredient 3]

**Steps:**
1. [step 1]
2. [step 2]
3. [step 3]

**Frequency:** [how often]

TONE:
• Professional, clear, friendly
• No invented products or ingredients
• Always provide safe skincare guidance (patch test + sunscreen when using acids/retinol)
• Format like ChatGPT: clean, scannable, well-structured

USER CONTEXT:`;

  // Add user context
  if (userSkinType) {
    systemPrompt += `\n- Skin Type: ${userSkinType}`;
  }
  if (userAcneSeverity) {
    systemPrompt += `\n- Acne Severity: ${userAcneSeverity}`;
  }
  if (userGlowScore !== null) {
    systemPrompt += `\n- GlowScore™: ${userGlowScore}/100`;
  }
         if (userProfile?.dietaryPreference) {
           systemPrompt += `\n- Dietary Preference: ${userProfile.dietaryPreference}`;
         }
         if (userProfile?.gender) {
           const genderText = userProfile.gender === 'male' ? 'Male' : 
                            userProfile.gender === 'female' ? 'Female' : 
                            'Prefer not to say';
           systemPrompt += `\n- Gender: ${genderText}`;
         }
  
  // Add product context (for reference only - NOT a restriction)
  if (visibleProducts && visibleProducts.length > 0) {
    systemPrompt += `\n\nUSER'S RECOMMENDED PRODUCTS (context only - use these ONLY when user specifically asks about "my recommendations", "from my scan", or "these products". You can still answer questions about ANY other products, brands, or topics):`;
    visibleProducts.slice(0, 10).forEach((p: any, i: number) => {
      systemPrompt += `\n${i + 1}. ${p.brand} ${p.productCategory}${p.price ? ` — ${p.price}` : ''}`;
      systemPrompt += `\n   - Key ingredients: ${p.keyIngredients?.slice(0, 3).join(', ') || 'N/A'}`;
      systemPrompt += `\n   - Why it helps: ${p.ingredientBenefits?.split('.')[0] || 'N/A'}`;
    });
  } else if (recommendedProducts.length > 0) {
    systemPrompt += `\n\nUSER'S RECOMMENDED PRODUCTS (context only - use these ONLY when user specifically asks about "my recommendations" or "from my scan". You can still answer questions about ANY other products, brands, or topics):`;
    recommendedProducts.slice(0, 10).forEach((p: any, i: number) => {
      systemPrompt += `\n${i + 1}. ${p.brand} ${p.productCategory}${p.price ? ` — ${p.price}` : ''}`;
      systemPrompt += `\n   - Key ingredients: ${p.keyIngredients?.slice(0, 3).join(', ') || 'N/A'}`;
      systemPrompt += `\n   - Why it helps: ${p.ingredientBenefits?.split('.')[0] || 'N/A'}`;
    });
  }
  
         // Add gender-based product filtering instructions
         if (userProfile?.gender === 'male') {
           systemPrompt += `\n\nGENDER-BASED PRODUCT FILTERING:\n- User is Male: Only recommend products specifically designed for men (men's skincare, grooming products, etc.). Avoid women-specific products.`;
         } else if (userProfile?.gender === 'female') {
           systemPrompt += `\n\nGENDER-BASED PRODUCT FILTERING:\n- User is Female: Only recommend products designed for women. Avoid men-specific products (grooming, shaving, beard care, etc.).`;
         } else if (userProfile?.gender === 'prefer_not_to_say') {
           systemPrompt += `\n\nGENDER-BASED PRODUCT FILTERING:\n- User prefers not to specify gender: Recommend products based on skin type, skin condition, ingredient sensitivities, and areas treated. Do not filter by gender.`;
         }

         systemPrompt += `\n\nCRITICAL GUIDELINES:
- You MUST answer ANY question about skincare, diet, nutrition, or skin health. Do NOT refuse or deflect
- NEVER say "I don't have that information", "I can't find that", "not in my database", or similar - you have extensive knowledge, USE IT
- Answer completely: diet plans get full 7-day menus; routines get full AM/PM steps; product questions get detailed info
- The recommended products list above is ONLY when users ask "my recommendations" - you can still answer about ANY other product or topic
- When asked about a product/brand, use the user's exact spelling in your response. Do not "correct" it (e.g. user says "Based bodyworks" → you say "Based bodyworks", not "Basis")
- Use the exact formats shown above (diet plan, routine, product, DIY mask)
- Be helpful, professional, and accurate. Never invent ingredients, but discuss real products that exist`;

  try {
    // Call OpenAI API (with optional image for vision)
    const response = await callOpenAI(systemPrompt, userMessage, conversationHistory || [], imageDataUrl);
    return response;
  } catch (error: any) {
    console.error('OpenAI API error in generateAIResponse:', error);
    
    // Provide more specific error messages based on the error type
    const errorMessage = error?.message || 'Unknown error';
    
    if (errorMessage.includes('API key')) {
      return `⚠️ Configuration Error: The OpenAI API key is not configured. Please contact support.`;
    } else if (errorMessage.includes('rate limit')) {
      return `⏱️ The AI service is currently busy. Please wait a moment and try again.`;
    } else if (errorMessage.includes('Network error') || errorMessage.includes('fetch')) {
      return `🌐 Connection Error: Please check your internet connection and try again.`;
    } else if (errorMessage.includes('temporarily unavailable') || errorMessage.includes('500')) {
      return `🔧 The AI service is temporarily unavailable. Please try again in a few moments.`;
    } else {
      // Show the actual error message for debugging, but keep it user-friendly
      return `I apologize, but I encountered an issue: ${errorMessage}. Please try again in a moment. If the issue persists, you can still access your personalized recommendations in the Results tab.`;
    }
  }
};
export const VIPChatbot: React.FC = () => {
  const { tier } = useSubscription();
  const { userProfile } = useAWSAuth();
  const { latestScan } = useLatestScan();
  const [isOpen, setIsOpen] = useState(false);
  const [messages, setMessages] = useState<Message[]>([
    {
      id: '1',
      text: "Hello! 👋 I'm Glow Bot, your AI skin care assistant. I'm here 24/7 to help with any questions about your skin, skincare routine, products, diet, or anything else! What would you like to know?",
      isUser: false,
      timestamp: new Date(),
    },
  ]);
  const [inputValue, setInputValue] = useState('');
  const [isTyping, setIsTyping] = useState(false);
  const messagesEndRef = useRef<HTMLDivElement>(null);
  const messagesContainerRef = useRef<HTMLDivElement>(null);

  const scrollToBottom = () => {
    messagesEndRef.current?.scrollIntoView({ behavior: 'smooth' });
  };

  // Always call all hooks - move useEffect before early return
  useEffect(() => {
    if (tier === 'elite') {
      scrollToBottom();
    }
  }, [messages, isTyping, tier]);

  // Only show for Elite users - check after all hooks are called
  if (tier !== 'elite') {
    return null;
  }

  const handleSend = async () => {
    if (!inputValue.trim()) return;

    const userMessage: Message = {
      id: Date.now().toString(),
      text: inputValue,
      isUser: true,
      timestamp: new Date(),
    };

    setMessages(prev => [...prev, userMessage]);
    setInputValue('');
    setIsTyping(true);

    // Call async AI response
    try {
      const recentHistory = messages.slice(-10).map(m => ({
        text: m.text,
        isUser: m.isUser,
      }));
      const userGender = userProfile?.gender;
      const visibleProducts = latestScan ? getRecommendedProductsFromScan(latestScan, 20, userGender) : undefined;
      
      const responseText = await generateAIResponse(userMessage.text, userProfile, latestScan, recentHistory, visibleProducts);
      const aiResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: responseText,
        isUser: false,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, aiResponse]);
      setIsTyping(false);
    } catch (error) {
      console.error('Error generating AI response:', error);
      const errorResponse: Message = {
        id: (Date.now() + 1).toString(),
        text: 'I apologize, but I encountered an error. Please try again.',
        isUser: false,
        timestamp: new Date(),
      };
      setMessages(prev => [...prev, errorResponse]);
      setIsTyping(false);
    }
  };

  const handleKeyPress = (e: React.KeyboardEvent) => {
    if (e.key === 'Enter' && !e.shiftKey) {
      e.preventDefault();
      handleSend();
    }
  };

  return (
    <>
      <ChatButton $isOpen={isOpen} onClick={() => setIsOpen(!isOpen)}>
        {isOpen ? <X /> : <MessageCircle />}
      </ChatButton>

      <ChatContainer $isOpen={isOpen}>
        <ChatHeader>
          <ChatHeaderContent>
            <Sparkles size={20} />
            <div>
              <ChatTitle>Glow Bot</ChatTitle>
              <VIPBadge>24/7 Support</VIPBadge>
            </div>
          </ChatHeaderContent>
          <CloseButton onClick={() => setIsOpen(false)}>
            <X />
          </CloseButton>
        </ChatHeader>

        <MessagesContainer ref={messagesContainerRef}>
          {messages.map((message) => (
            <Message key={message.id} $isUser={message.isUser}>
              <MessageAvatar $isUser={message.isUser}>
                {message.isUser ? <User /> : <Bot />}
              </MessageAvatar>
              <MessageBubble $isUser={message.isUser}>
                {message.text}
              </MessageBubble>
            </Message>
          ))}
          {isTyping && (
            <Message $isUser={false}>
              <MessageAvatar $isUser={false}>
                <Bot />
              </MessageAvatar>
              <TypingIndicator>
                <span />
                <span />
                <span />
              </TypingIndicator>
            </Message>
          )}
          <div ref={messagesEndRef} />
        </MessagesContainer>

        <InputContainer>
          <Input
            id="glow-bot-chat-input"
            name="glowBotMessage"
            placeholder="Ask me anything about your skin..."
            value={inputValue}
            onChange={(e) => setInputValue(e.target.value)}
            onKeyDown={(e) => {
              if (e.key === 'Enter' && !e.shiftKey) {
                e.preventDefault();
                handleKeyPress(e as any);
              }
            }}
            rows={1}
            aria-label="Glow Bot chat message"
          />
          <SendButton variant="primary" onClick={handleSend} disabled={!inputValue.trim()}>
            <Send size={18} />
          </SendButton>
        </InputContainer>
      </ChatContainer>
    </>
  );
};

